import React, {useCallback, useMemo} from 'react'
import {useGameStore} from '../../store/GameStoreContext'
// eslint-disable-next-line no-restricted-imports
import {audioManager, Sound} from '../../../../../code/client/shared/scripts/Sound'
import {PlayerColor} from '@colonist/game-states'
import {ModeSettingEnum} from '@colonist/types'
import {useHotkeys} from 'react-hotkeys-hook'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {useIsDiceActive, useIsDiceVisible} from './utils'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import {UIGameDiceGroup} from './UIGameDiceGroup'
import {StorageWrapper} from '../../utils/storage'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'

export interface UIGameDiceGroupContainerProps {
    playerColor: PlayerColor
    size?: 'medium' | 'large'
    className?: string | undefined
    disableDiceSound?: boolean
}

export function UIGameDiceGroupContainer({
    playerColor,
    size = 'medium',
    className,
    disableDiceSound = false,
}: UIGameDiceGroupContainerProps) {
    const diceState = useGameStore((state) => state.gameState.diceState)
    const eventDiceState = useGameStore((state) => state.gameState.mechanicEventDiceState)
    const currentState = useGameStore((state) => state.gameState.currentState)
    const myColor = useGameStore((state) => state.myColor)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const currentStateValidator = useCurrentStateValidator()

    const visible = useIsDiceVisible(currentState, currentStateValidator.currentTurnPlayers, playerColor)
    const {active, helperText} = useIsDiceActive(currentState, playerColor, myColor, currentStateValidator.currentTurnPlayers ?? [])
    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
    const beginnerHintType = useGameStore((state) => state.gameBeginnerHint.beginnerHintType)
    const setBeginnerHintType = useGameStore((state) => state.gameBeginnerHint.setBeginnerHintType)
    const {sendClickedDice, sendDiceNumberAnimationEnd} = useUIGameSocketContext()
    const isPaused = useGameStore((state) => state.gamePause.isPaused)
    const isReplay = useIsReplay()
    const isMyTurn = currentStateValidator.currentTurnPlayers.includes(myColor)
    const enableDiceHelper = isBeginnerMode
    const disableDiceSoundLocal = disableDiceSound || isReplay
    const isColonistRush = modeSetting == ModeSettingEnum.ColonistRush4P

    const soundOnClick = useMemo(() => {
        if(isColonistRush) return Sound.SoundType.Click // TODO add custom sound for Colonist Rush
        return Sound.SoundType.Click
    }, [isColonistRush])

    const clearBeginnerHint = () => {
        if(beginnerHintType == 'RollDice') {
            setBeginnerHintType(undefined)
        }
    }

    const handleClick = useCallback(() => {
        if(isPaused) return
        if(isReplay) return
        if(!isMyTurn) return
        audioManager.play(soundOnClick)
        if(active) {
            clearBeginnerHint()
            sendClickedDice()
        }
    }, [isPaused, isReplay, isMyTurn, active, soundOnClick])

    useHotkeys('Space', () => {
        if(StorageWrapper.getKeyboardShortcuts()) {
            handleClick()
        }
    }, [handleClick])

    return (
        <UIGameDiceGroup
            size={size}
            className={className}
            onClick={handleClick}
            diceState={diceState}
            eventDiceState={eventDiceState}
            active={active}
            visible={visible}
            enableDiceHelper={enableDiceHelper}
            helperText={helperText}
            isMyTurn={isMyTurn}
            disableDiceSound={disableDiceSoundLocal}
            onDiceHelperAnimationEnd={sendDiceNumberAnimationEnd}
        />
    )
}
